// rowModel.ts — reine (DOM-freie) Logik für ausklappbare Zeilen (Child-Rows) der
// DataTable-Engine. Ausgelagert, damit das Auf-/Zuklapp-Verhalten in der
// node-Testumgebung geprüft werden kann. Generisch/opt-in: ohne rowChildren/
// expandedKeys liefert flattenRows exakt die Eltern-Zeilen in Eingangsreihenfolge
// (bestehende DataTable-Nutzer bleiben unverändert).

import type { Key } from "react";

/** Eine gerenderte (flache) Zeile: Eltern- oder eingerückte Kind-Zeile. */
export type FlatRow<T> = {
  row: T;
  /** React-Key dieser gerenderten Zeile (bei Kindern zusammengesetzt → kollisionsfrei). */
  key: Key;
  /** Index, der dem Zell-Renderer übergeben wird: Eltern → Position in `rows`,
   *  Kind → Position in der Kinder-Liste des Elternteils. */
  index: number;
  isChild: boolean;
  /** Key der Eltern-Zeile (nur bei Kindern gesetzt). */
  parentKey?: Key;
};

export type FlattenOptions<T> = {
  expandedKeys?: Iterable<Key>;
  rowChildren?: (row: T, index: number) => T[];
};

/**
 * Flacht Eltern-Zeilen + (bei Expansion) deren Kind-Zeilen in eine Render-Liste.
 * Kinder werden nur eingefügt, wenn der Eltern-Key in `expandedKeys` liegt und
 * `rowChildren` eine nicht-leere Liste liefert.
 */
export function flattenRows<T>(
  rows: readonly T[],
  rowKey: (row: T, index: number) => Key,
  opts?: FlattenOptions<T>,
): FlatRow<T>[] {
  const expanded = opts?.expandedKeys ? new Set<Key>(opts.expandedKeys) : null;
  const rowChildren = opts?.rowChildren;
  const out: FlatRow<T>[] = [];
  rows.forEach((row, i) => {
    const key = rowKey(row, i);
    out.push({ row, key, index: i, isChild: false });
    if (!expanded || !rowChildren || !expanded.has(key)) return;
    const kids = rowChildren(row, i) ?? [];
    kids.forEach((child, j) => {
      const childKey = rowKey(child, j);
      out.push({
        row: child,
        // Zusammengesetzter Key: verhindert Kollisionen, falls ein Kind zufällig
        // denselben rowKey wie eine Eltern-Zeile hätte.
        key: `${String(key)}::${String(childKey)}`,
        index: j,
        isChild: true,
        parentKey: key,
      });
    });
  });
  return out;
}
